/**
 * The contact form's message limit, in one place both halves read.
 *
 * Its own module for the reason `lib/contact.ts` gives: the Worker and the
 * component both need it, and the worker tsconfig cannot import anything that
 * reads `import.meta.env`. No imports, so `scripts/gate-test.mjs` can load it
 * on Node's type-stripping as well.
 *
 * Until 2026-10-25 the two halves did not agree: the Worker said 4,000 and the
 * textarea said nothing, and `str()` in `worker/tools.ts` answers `null` for a
 * value over its limit — so a 4,001-character message was not shortened, it
 * vanished, and the row was saved without it while the visitor was thanked.
 * Nobody had lost one yet (116 submissions, the longest 1,594 characters), which
 * is the only reason it was found by reading rather than by a customer.
 */

/** UTF-16 code units, which is what `maxLength` and `.length` both count. */
export const FORM_MESSAGE_MAX = 4000

/**
 * Trim, and cut anything past `max` instead of dropping it — with a line saying
 * so, because the person reading it in Inbound must not take a cut-off sentence
 * for the end of the message. The marker rides AFTER the `max` characters: the
 * limit is what the visitor was allowed to write, not a column width (the column
 * is `text`).
 *
 * A browser cannot reach this through the form, whose textarea carries the same
 * `maxLength`. What can is anything that posts to the Worker by other means,
 * and an old cached page from before the limit was on the textarea.
 */
export function clipText(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null
  const t = v.trim()
  if (!t) return null
  if (t.length <= max) return t
  // Never end on half of a surrogate pair: an emoji cut in two is a lone
  // surrogate, which is not valid UTF-16 and turns into U+FFFD on the way out.
  const code = t.charCodeAt(max - 1)
  const cut = code >= 0xd800 && code <= 0xdbff ? max - 1 : max
  return `${t.slice(0, cut)}\n\n[… cut by the website form at ${max} of ${t.length} characters]`
}
